"use client"

import { useState, useEffect, useRef } from "react"
import { Button } from "../ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Input } from "../ui/input"
import Label from "../ui/label"
import { Save, Lock } from "lucide-react";
import { toast } from 'react-hot-toast'
import { visitTypeApiService } from "../../apis/visitTypeService"
import CustomDropdown from '../ui/custom-dropdown';
import DatePicker from '../ui/date-picker';
import { useAuth } from "../../context/AuthContext"
import { attachPronounsToPatientPayload, resolvePronounsFromPatient } from "../../utils/inferPronouns"

/** YYYY-MM-DD for "today" in America/New_York — avoids UTC `toISOString()` drift for US DOS. */
function getTodayAmericaISO() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date())
  const y = parts.find((p) => p.type === "year")?.value
  const m = parts.find((p) => p.type === "month")?.value
  const d = parts.find((p) => p.type === "day")?.value
  if (!y || !m || !d) return ""
  return `${y}-${m}-${d}`
}

const DATE_OF_SERVICE_FUTURE_DAYS = 30

function normalizeDateOfService(value) {
  const candidate = typeof value === "string" ? value.trim() : ""
  if (!candidate) return getTodayAmericaISO()
  const isoDay = /^(\d{4}-\d{2}-\d{2})/.exec(candidate)?.[1]
  if (isoDay) return isoDay
  return getTodayAmericaISO()
}

function shiftIsoByDays(iso, deltaDays) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""))
  if (!match) return ""
  const shifted = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]) + deltaDays,
  )
  const year = shifted.getFullYear()
  const month = String(shifted.getMonth() + 1).padStart(2, "0")
  const day = String(shifted.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const AUTOSAVE_DEBOUNCE_MS = 1000

function validateFormData(data) {
  const errors = []

  if (!data.dateOfService) {
    errors.push("Date of Service is required")
  } else {
    const todayAmerica = getTodayAmericaISO()
    const maxIso = shiftIsoByDays(todayAmerica, DATE_OF_SERVICE_FUTURE_DAYS)
    const dateOfService = normalizeDateOfService(data.dateOfService)
    if (dateOfService && maxIso && dateOfService > maxIso) {
      errors.push("Date of Service cannot be more than 30 days in the future")
    }
  }

  if (!data.name.trim()) {
    errors.push("Patient name is required")
  }

  const ageStr = String(data.age ?? "").trim()
  if (ageStr !== "" && (isNaN(data.age) || parseInt(ageStr, 10) <= 0)) {
    errors.push("Please enter a valid age")
  }

  const visitTrim = String(data.visitType ?? "").trim()
  if (!visitTrim) {
    errors.push("Type of visit is required")
  }

  return errors
}

function buildPatientPayloadFromFormData(data) {
  const errors = validateFormData(data)
  if (errors.length > 0) return null

  const nameParts = String(data.name).trim().split(" ")
  const firstName = nameParts[0] || ""
  const lastName = nameParts.slice(1).join(" ") || ""
  const genderTrim = String(data.gender ?? "").trim()
  const ageStr = String(data.age ?? "").trim()
  let age = null
  if (ageStr !== "") {
    const parsed = parseInt(ageStr, 10)
    if (!isNaN(parsed) && parsed > 0) age = parsed
  }

  const base = {
    dateOfService: data.dateOfService,
    name: data.name,
    firstName,
    lastName,
    visitType: String(data.visitType).trim(),
  }
  if (genderTrim) base.gender = genderTrim
  if (age != null) base.age = age

  return attachPronounsToPatientPayload(base)
}

function serializePatientPayload(p) {
  return JSON.stringify({
    dateOfService: p.dateOfService,
    name: p.name,
    firstName: p.firstName,
    lastName: p.lastName,
    gender: p.gender ?? "",
    age: p.age ?? "",
    visitType: p.visitType ?? "",
    pronouns: p.pronouns,
    pronounsSource: p.pronounsSource,
  })
}

export default function PatientContextForm({
  context,
  updateContext,
  onSave,
  isDemoMode = false,
  demoAnimationNonce = 0,
  isTourPatientStep = false,
}) {
  const [formData, setFormData] = useState(() => ({
    dateOfService: getTodayAmericaISO(),
    name: "",
    gender: "",
    age: "",
    visitType: "",
  }))
  const [visitTypeOptions, setVisitTypeOptions] = useState([])
  const [loadingVisitTypes, setLoadingVisitTypes] = useState(false)
  const lastSavedPayloadRef = useRef(null)
  const autosaveTimerRef = useRef(null)
  const demoAnimationKeyRef = useRef(null)
  const updateContextRef = useRef(updateContext)
  updateContextRef.current = updateContext

  // Get user info from auth context
  const { currentUser } = useAuth()
  
  // Role check based on your backend setup
  const hasEditAccess = () => {
    const user = currentUser;
    if (!user) return false;
    
    // Get role ID or role name from your backend structure
    const roleId = user.profile?.roleId || user.roleId || user.role?.id || user.role;
    const roleName = (user.profile?.roleName || user.roleName || user.role?.name || user.role || "").toLowerCase();
    
    
    // Based on your database schema and the console output:
    // we're getting role names/IDs as strings: 'user', 'admin', 'scribe_parttime', 'scribe_fulltime'
    
    // Check for User - role name contains "user"
    if (roleId === 1 || roleId === "1" || roleName.includes("user") || roleId === "user") {
      return true;
    }
    
    // Check for Admin - role name contains "admin"
    if (roleId === 2 || roleId === "2" || roleName.includes("admin") || roleId === "admin") {
      return true;
    }
    
    // Check for Scribe (Full-time) - role name contains "scribe_fulltime" or "fulltime"
    if (roleId === 4 || roleId === "4" || 
        roleName.includes("scribe_fulltime") ||
        roleName.includes("scribe full-time") ||
        roleName.includes("full-time scribe") ||
        roleName.includes("fulltime") ||
        roleId === "scribe_fulltime") {
      return true;
    }
    
    // Check for Scribe (Part-time) - role name contains "scribe_parttime" or "parttime"
    if (roleId === 3 || roleId === "3" || 
        roleName.includes("scribe_parttime") ||
        roleName.includes("scribe part-time") ||
        roleName.includes("part-time scribe") ||
        roleName.includes("parttime") ||
        roleId === "scribe_parttime") {
      return false; // Part-time scribes CANNOT edit
    }
    
    return false;
  };
  
  const canEdit = hasEditAccess();

  // Get user role display name
  const getUserRoleDisplay = () => {
    const user = currentUser;
    if (!user) return "Unknown Role";
    
    const roleId = user.profile?.roleId || user.roleId || user.role?.id || user.role;
    const roleName = (user.profile?.roleName || user.roleName || user.role?.name || user.role || "").toLowerCase();
    
    // Match your database role structure
    if (roleId === 1 || roleId === "1" || roleName.includes("user") || roleId === "user") {
      return "User";
    }
    if (roleId === 2 || roleId === "2" || roleName.includes("admin") || roleId === "admin") {
      return "Admin";
    }
    if (roleId === 3 || roleId === "3" || roleName.includes("scribe_parttime") || roleName.includes("parttime") || roleId === "scribe_parttime") {
      return "Scribe (Part-time)";
    }
    if (roleId === 4 || roleId === "4" || roleName.includes("scribe_fulltime") || roleName.includes("fulltime") || roleId === "scribe_fulltime") {
      return "Scribe (Full-time)";
    }
    
    // Fallback based on role name
    if (roleName.includes("scribe")) {
      if (roleName.includes("part-time") || roleName.includes("parttime")) {
        return "Scribe (Part-time)";
      }
      return "Scribe";
    }
    
    return "User";
  };

  const userRole = getUserRoleDisplay();
  const isPartTimeScribe = userRole === "Scribe (Part-time)";
  const shouldAnimateTourData = isDemoMode || isTourPatientStep;

  const pronounHint =
    formData.name?.trim() && !String(formData.gender ?? "").trim()
      ? (() => {
          const p = resolvePronounsFromPatient({ name: formData.name, gender: formData.gender });
          return `Gender is optional. If left blank, the SOAP note will use ${p.pronouns} (inferred from the full patient name).`;
        })()
      : null;

  // Gender options
  const genderOptions = ["Male", "Female", "Other"];

  useEffect(() => {
    const loadVisitTypeOptions = async () => {
      setLoadingVisitTypes(true);
      try {
        const response = await visitTypeApiService.getVisitTypeOptions();
        if (response.success) {
          setVisitTypeOptions(response.data.visitTypes);
        }
      } catch (error) {
        console.error('Failed to load visit type options:', error);
        toast.error('Failed to load visit type options');
      } finally {
        setLoadingVisitTypes(false);
      }
    };

    loadVisitTypeOptions();
  }, []);

  useEffect(() => {
    if (context) {
      const newFormData = {
        dateOfService: normalizeDateOfService(context.dateOfService),
        name: context.name || (context.firstName && context.lastName)
          ? `${context.firstName} ${context.lastName}`
          : context.name || "",
        gender: context.gender || "",
        age:
          context.age != null && context.age !== ""
            ? String(context.age)
            : "",
        visitType: context.visitType || ""
      };

      if (shouldAnimateTourData) {
        //   from Step 2 → Step 3, which lets the cleanup cancel the loop and
        //   the new run populate the form with the real values.
        const animationKey = `${demoAnimationNonce}:${JSON.stringify(newFormData)}:step=${isTourPatientStep ? 2 : "other"}`
        if (demoAnimationKeyRef.current === animationKey) return
        demoAnimationKeyRef.current = animationKey

        let cancelled = false
        let animationTimer = null

       
        const TYPING_MS = 110
        const START_MS = 400
        const REVEAL_MS = 350

        
        const nameValue = String(newFormData.name || "")
        const finalAge = newFormData.age
        const finalGender = newFormData.gender
        const finalVisitType = newFormData.visitType

        const runCycle = () => {
          if (cancelled) return

        
          setFormData((prev) => ({
            ...prev,
            dateOfService: newFormData.dateOfService,
            name: "",
            age: "",
            gender: "",
            visitType: "",
          }))

          let characterIndex = 0

          const typeNameCharacter = () => {
            if (cancelled) return

            // Name fully typed → keep it constant and reveal the rest.
            if (characterIndex >= nameValue.length) {
              animationTimer = window.setTimeout(() => {
                if (cancelled) return
                setFormData((prev) => ({
                  ...prev,
                  // Name is left exactly as it finished typing — constant.
                  name: nameValue,
                  // Remaining fields appear in one shot.
                  age: finalAge,
                  gender: finalGender,
                  visitType: finalVisitType,
                }))
              }, REVEAL_MS)
              return
            }

            characterIndex += 1
            setFormData((prev) => ({
              ...prev,
              name: nameValue.slice(0, characterIndex),
            }))
            animationTimer = window.setTimeout(typeNameCharacter, TYPING_MS)
          }

          animationTimer = window.setTimeout(typeNameCharacter, START_MS)
        }

        runCycle()

        return () => {
          cancelled = true
          if (animationTimer) window.clearTimeout(animationTimer)
        }
      }
      demoAnimationKeyRef.current = null
      setFormData(newFormData);
      const synced = buildPatientPayloadFromFormData(newFormData)
      lastSavedPayloadRef.current = synced ? serializePatientPayload(synced) : null
    }
  }, [context, visitTypeOptions, shouldAnimateTourData, demoAnimationNonce, isTourPatientStep])

  useEffect(() => {
    if (!canEdit || shouldAnimateTourData) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)

    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      const payload = buildPatientPayloadFromFormData(formData)
      if (!payload) return
      const serialized = serializePatientPayload(payload)
      if (serialized === lastSavedPayloadRef.current) return
      lastSavedPayloadRef.current = serialized
      updateContextRef.current(payload)
      toast.success("Patient details saved successfully", {
        id: "patient-context-autosave",
        duration: 2200,
      })
    }, AUTOSAVE_DEBOUNCE_MS)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
  }, [formData, canEdit, shouldAnimateTourData])

  const handleChange = (e) => {
    // Only allow changes if user has edit access
    if (!canEdit) {
      if (isPartTimeScribe) {
        toast.error("As a part-time scribe, you can only view patient information.");
      } else {
        toast.error("You don't have permission to edit patient information.");
      }
      return;
    }
    
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const validateForm = () => validateFormData(formData)

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Check if user has edit access
    if (!canEdit) {
      if (isPartTimeScribe) {
        toast.error("As a part-time scribe, you cannot save patient information. Only Admins, Users, and Full-time Scribes can edit.");
      } else {
        toast.error("You don't have permission to save patient information.");
      }
      return;
    }

    const validationErrors = validateForm()

    if (validationErrors.length > 0) {
      validationErrors.forEach(error => {
        toast.error(error)
      })
      return
    }

    const patientData = buildPatientPayloadFromFormData(formData)
    if (!patientData) return

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
    }

    lastSavedPayloadRef.current = serializePatientPayload(patientData)
    await updateContext(patientData)
    toast.dismiss("patient-context-autosave")
    await onSave(patientData);
    toast.success('Patient details saved successfully!')
  }

  // Gender is selection-only (no free text).
  const savedGender = String(formData.gender ?? "").trim();
  const matchedGenderOption = genderOptions.find(o => o.toLowerCase() === savedGender.toLowerCase());
  const selectedGenderValue = matchedGenderOption || savedGender;
  const genderDropdownOptions = [
    ...genderOptions.map(o => ({ value: o, label: o })),
    ...(savedGender && !matchedGenderOption ? [{ value: savedGender, label: savedGender }] : []),
  ];
  // Function to handle form field changes with edit access check
  const handleValueChange = (name, value) => {
    if (!canEdit) {
      if (isPartTimeScribe) {
        toast.error("As a part-time scribe, you can only view patient information.");
      } else {
        toast.error("You don't have permission to edit patient information.");
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const FIELD_HEIGHT = "h-11";
  const FIELD_RADIUS = "!rounded-full";
  const FIELD_PADDING = "px-4";
  const FIELD_BORDER = "border border-gray-300 shadow-sm";
  const FIELD_FOCUS =
    "focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50";
  const FIELD_BASE = `mt-1 w-full ${FIELD_HEIGHT} ${FIELD_RADIUS} ${FIELD_PADDING} ${FIELD_BORDER} ${FIELD_FOCUS} box-border`;
  const DROPDOWN_BUTTON_BASE = `mt-1 w-full ${FIELD_HEIGHT} ${FIELD_RADIUS} ${FIELD_PADDING} ${FIELD_BORDER} ${FIELD_FOCUS} box-border flex items-center justify-between leading-none`;

  return (
    <Card
      data-tour="patient-form"
      className={`border-0 shadow-md rounded-xl bg-white mt-4 ${
        isTourPatientStep
          ? "ring-4 ring-blue-400 ring-offset-4 transition-shadow duration-300"
          : ""
      }`}
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-xl font-semibold text-gray-800">Patient Information</CardTitle>
        <CardDescription className="text-sm text-gray-500">
          {canEdit 
            ? "Patient name and type of visit are required. Gender and age are optional but recommended when known."
            : `View patient information (${userRole} - Read-only)`
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ===== REVISED LAYOUT: Row 1 – Name + Visit; Row 2 – Gender + Age ===== */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-gray-700">Patient Full Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter patient full name"
                required
                disabled={!canEdit}
                className={`${FIELD_BASE} ${!canEdit ? "bg-gray-100 cursor-not-allowed" : ""}`}
              />
              {!canEdit && (
                <p className="text-sm text-gray-500 mt-1">Read-only ({userRole})</p>
              )}
            </div>

            <div>
              <Label htmlFor="visitType" className="text-sm font-medium text-gray-700">Type of Visit *</Label>
              <CustomDropdown
                options={visitTypeOptions}
                value={formData.visitType}
                onValueChange={(value) => handleValueChange("visitType", value)}
                placeholder={loadingVisitTypes ? "Loading visit types..." : "Select type of visit"}
                disabled={loadingVisitTypes || !canEdit}
                buttonClassName={`${DROPDOWN_BUTTON_BASE} ${!canEdit ? "bg-gray-500 cursor-not-allowed" : ""}`}
              />
              {!canEdit && (
                <p className="text-sm text-gray-500 mt-1">Read-only ({userRole})</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div>
              <Label htmlFor="gender" className="text-sm font-medium text-gray-700">Gender (optional)</Label>
              <CustomDropdown
                options={genderDropdownOptions}
                value={selectedGenderValue}
                onValueChange={(value) => handleValueChange("gender", value)}
                placeholder="Male, Female, Other"
                disabled={!canEdit}
                buttonClassName={`${DROPDOWN_BUTTON_BASE} ${!canEdit ? "bg-gray-500 cursor-not-allowed" : ""}`}
              />
              {!canEdit && (
                <p className="text-sm text-gray-500 mt-1">Read-only ({userRole})</p>
              )}
            </div>

            <div>
              <Label htmlFor="age" className="text-sm font-medium text-gray-700">Age (optional)</Label>
              <Input
                id="age"
                name="age"
                type="number"
                value={formData.age}
                onChange={handleChange}
                placeholder="Years"
                min="0"
                disabled={!canEdit}
                className={`${FIELD_BASE} ${!canEdit ? "bg-gray-100 cursor-not-allowed" : ""}`}
              />
              {!canEdit && (
                <p className="text-sm text-gray-500 mt-1">Read-only ({userRole})</p>
              )}
            </div>
          </div>

          {/* === Date of Service (DOS) is now hidden – we keep it in state for backend === */}
          {/* <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="dateOfService">Date of Service (DOS) *</Label>
              <DatePicker
                value={formData.dateOfService}
                onChange={handleDateChange}
                placeholder="Select date of service"
                maxDate={getTodayAmericaISO()}
                className={`w-full text-sm p-2 rounded-md border ${!canEdit ? "bg-gray-100 cursor-not-allowed" : ""}`}
                calendarClassName="!text-sm !p-1 !w-[220px]"
                disabled={!canEdit}
              />
              {!canEdit && (
                <p className="text-sm text-gray-500 mt-1">Read-only ({userRole})</p>
              )}
            </div>
          </div> */}

          {pronounHint && canEdit && (
            <p className="text-sm text-gray-500 -mt-2">{pronounHint}</p>
          )}

          <div className="text-center pt-1">
            {canEdit ? (
              <Button 
                type="submit" 
                className="bg-gradient-to-r from-blue-600 to-teal-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 !rounded-full px-6 py-2 font-medium"
              >
                <Save className="mr-2 h-4 w-4" /> Save Patient Info
              </Button>
            ) : (
              <div className="space-y-2">
                <Button 
                  type="button"
                  disabled
                  className="bg-gray-300 text-gray-600 cursor-not-allowed !rounded-full px-6 py-2 font-medium w-full"
                >
                  <Lock className="mr-2 h-4 w-4" /> Save Patient Info
                </Button>
                <div className={`text-center p-3 rounded-md ${isPartTimeScribe ? 'bg-blue-50 border border-blue-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <p className={`text-sm font-medium ${isPartTimeScribe ? 'text-blue-800' : 'text-yellow-800'}`}>
                    {isPartTimeScribe 
                      ? "View-only access for part-time scribes"
                      : "You don't have permission to save patient information."
                    }
                  </p>
                  <p className={`text-sm mt-1 ${isPartTimeScribe ? 'text-blue-700' : 'text-yellow-700'}`}>
                    {isPartTimeScribe 
                      ? "As a part-time scribe, you can view patient information but cannot edit it. Only Admins, Users, and Full-time Scribes can modify patient details."
                      : `Your role (${userRole}) doesn't have editing permissions. Please contact your administrator.`
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  )
}